import React, { useContext } from 'react';
import { v4 } from 'uuid';
import { PdfTemplateInput } from '../../../types/pdf_template';
import { PdfProductMasterDataProperty } from './data/entities/PdfProductMasterData';
import { PdfProductTransactionProperty } from './data/entities/PdfProductTransaction';
import { PdfDataKey, PdfFieldEntity } from './data/PdfDataKey';
import { PdfFieldResolving } from './data/PdfFieldResolving';
import {
  PdfComponent,
  PdfFontFamily,
  PdfTableColumn,
  PdfTableComponent,
  PdfTextAlign,
  PdfComponentFontProperties,
  PdfOverflow,
  PdfTableType,
  PdfTemplateDocumentProperties,
  PdfOrientation,
  PdfPageFormat,
} from './PdfComponent';
import { PdfTemplateUtil } from './pdfTemplate.util';

const { mmToPx, pxToMm } = PdfTemplateUtil;

export enum PdfEditorPreviewPage {
  first = 'first',
  middle = 'middle',
  last = 'last',
}

export interface PdfTemplateEditorSettings {
  sidebarWidth: number;
  propertiesBarWidth: number;
  gridMode: boolean;
  zoom: number;
  sidebarOpen: boolean;
  gridMm: number;
  previewPage: PdfEditorPreviewPage;
}

export interface PdfTableDataProvider<T, E = any> {
  tableType: PdfTableType;
  columns: PdfDataKey<any>[];
  rowResolver: (entity?: T) => E[];
}

export function getDefaultTableColumnsForTableType(tableType: PdfTableType): PdfTableColumn[] {
  switch (tableType) {
    case PdfTableType.productTransaction:
      return [
        {
          id: v4(),
          weight: 3,
          align: PdfTextAlign.left,
          overflow: PdfOverflow.ellipsis,
          label: 'Product Name',
          width: 50,
          key: {
            entityType: PdfFieldEntity.productMasterData,
            property: PdfProductMasterDataProperty.productName,
          },
        },
        {
          id: v4(),
          weight: 3,
          align: PdfTextAlign.left,
          overflow: PdfOverflow.ellipsis,
          label: 'Product Number',
          width: 50,
          key: {
            entityType: PdfFieldEntity.productMasterData,
            property: PdfProductMasterDataProperty.productNumber,
          },
        },
        {
          id: v4(),
          weight: 1,
          align: PdfTextAlign.right,
          overflow: PdfOverflow.ellipsis,
          label: 'Quantity',
          width: 10,
          key: {
            entityType: PdfFieldEntity.productTransaction,
            property: PdfProductTransactionProperty.quantity,
          },
        },
      ];
  }
}

export interface PdfTemplateEditorContextValue<T> {
  input: PdfTemplateInput;
  data: PdfFieldResolving.Data;
  documentProperties: PdfTemplateDocumentProperties;
  // setDocumentProperties: React.Dispatch<React.SetStateAction<PdfTemplateDocumentProperties>>;
  updateDocumentProperties: (updates: Partial<PdfTemplateDocumentProperties>) => void;
  components: PdfComponent[];
  // setComponents: React.Dispatch<React.SetStateAction<PdfComponent[]>>;
  setComponents: (components: PdfComponent[]) => void;
  updateComponent: (id: string, updates: Partial<PdfComponent>) => void;
  deleteComponent: (id: string) => void;
  selectedId: string | null;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  selected: PdfComponent | undefined;
  editorSettings: PdfTemplateEditorSettings;
  setEditorSettings: React.Dispatch<React.SetStateAction<PdfTemplateEditorSettings>>;
  updateEditorSettings: (updates: Partial<PdfTemplateEditorSettings>) => void;
  dataKeys: PdfDataKey<any>[];
  imageDataKeys: PdfDataKey<any>[];
  tableProviders: PdfTableDataProvider<T>[];
  selectedEntity?: T;
  setSelectedEntity: React.Dispatch<React.SetStateAction<T | undefined>>;
  // Zoom-aware helpers
  mmToPxScaled: (mm: number) => number;
  pxToMmScaled: (px: number) => number;
  fsToPtScaled: (pt?: number) => number;
  gridSizePx: () => number;
  snapMm: (mm: number) => number;
  tableColumnWidthsPx: (component: PdfTableComponent) => number[];
  componentBoxPx: (comp: { x: number; y: number; width: number; height: number }) => {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  eventPointPxToMm: (
    clientX: number,
    clientY: number,
    canvasOffset: { left: number; top: number },
  ) => {
    xMm: number;
    yMm: number;
  };
  fontToCss: (font: PdfComponentFontProperties) => { [key: string]: string | number };
}

const defaultContext: PdfTemplateEditorContextValue<any> = {
  input: {} as any,
  data: {
    stockLocations: new Map<string, any>(),
    orders: new Map<string, any>(),
    bins: new Map<string, any>(),
    containers: new Map<string, any>(),
    productMasterData: new Map<string, any>(),
    productTransactions: new Map<string, any>(),
    lots: new Map<string, any>(),
    customFields: new Map<string, any>(),
    pdf: { page: 1, totalPages: 1 },
  },
  documentProperties: {
    font: { family: PdfFontFamily.helvetica, size: 11, color: '#111111' },
    pageFormat: PdfPageFormat.A4,
    orientation: PdfOrientation.portrait,
  },
  // setDocumentProperties: () => {},
  updateDocumentProperties: () => {},
  components: [],
  setComponents: () => {},
  updateComponent: () => {},
  deleteComponent: () => {},
  selectedId: null,
  setSelectedId: () => {},
  selected: undefined,
  editorSettings: {
    propertiesBarWidth: 330,
    sidebarWidth: 330,
    gridMode: true,
    zoom: 1,
    sidebarOpen: true,
    gridMm: 5,
    previewPage: PdfEditorPreviewPage.first,
  },
  setEditorSettings: () => {},
  updateEditorSettings: () => {},
  selectedEntity: undefined,
  setSelectedEntity: () => {},
  dataKeys: [],
  imageDataKeys: [],
  tableProviders: [],
  // Default no-op implementations; real ones provided by Provider below
  mmToPxScaled: (mm: number) => mmToPx(mm),
  pxToMmScaled: (px: number) => pxToMm(px),
  fsToPtScaled: (pt?: number) => pt ?? 11,
  gridSizePx: () => mmToPx(5),
  snapMm: (mm: number) => mm,
  tableColumnWidthsPx: (_component: PdfTableComponent) => [],
  componentBoxPx: (comp: { x: number; y: number; width: number; height: number }) => ({
    x: mmToPx(comp.x),
    y: mmToPx(comp.y),
    width: mmToPx(comp.width),
    height: mmToPx(comp.height),
  }),
  eventPointPxToMm: (clientX: number, clientY: number, canvasOffset: { left: number; top: number }) => ({
    xMm: pxToMm(clientX - canvasOffset.left),
    yMm: pxToMm(clientY - canvasOffset.top),
  }),
  fontToCss: (font: PdfComponentFontProperties) => ({ fontSize: `${font.size}pt`, color: font.color }),
};

export const PdfTemplateEditorContext = React.createContext<PdfTemplateEditorContextValue<any>>(defaultContext);

export function usePdfTemplateContext<T>() {
  const ctx = useContext(PdfTemplateEditorContext);
  if (!ctx) throw new Error('Component not within PdfTemplateEditorContext');
  return ctx as PdfTemplateEditorContextValue<T>;
}
